import { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, LogOut, CreditCard, ChevronUp, LifeBuoy } from 'lucide-react';
import { signOut as firebaseSignOut } from 'firebase/auth';
import { firebaseAuth } from '@/lib/firebase';
import { useAuth } from '@/lib/auth';
import { useUserProfile } from '@/hooks/use-learning-profile';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

interface SidebarUserMenuProps {
  isExpanded: boolean;
  onDropdownOpenChange?: (open: boolean) => void;
}

function getInitials(firstName?: string, fullName?: string, email?: string | null): string {
  if (firstName) return firstName.charAt(0).toUpperCase();
  if (fullName) return fullName.charAt(0).toUpperCase();
  if (email) return email.charAt(0).toUpperCase();
  return 'U';
}

/** Sidebar label: prefer first name, fall back to email username */
function getSidebarName(
  firstName?: string,
  email?: string | null
): string {
  if (firstName) return firstName;
  if (email) return email.split('@')[0];
  return 'User';
}

/** Dropdown header: show full name if available */
function getFullDisplayName(
  firstName?: string,
  fullName?: string,
  email?: string | null
): string {
  if (fullName) return fullName;
  if (firstName) return firstName;
  if (email) return email.split('@')[0];
  return 'User';
}

export const SidebarUserMenu = memo(function SidebarUserMenu({
  isExpanded,
  onDropdownOpenChange,
}: SidebarUserMenuProps) {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Fetch first/last name from profiles table (source of truth for display name).
  // Uses the canonical query key so onboarding's useUpdateUserProfile mutation
  // invalidates this cache when first_name is written. Without this, the sidebar
  // would show the email prefix until a manual page refresh.
  const { data: profile } = useUserProfile();

  // Prefer DB profile name, fall back to Firebase displayName
  const firstName = profile?.first_name || user?.user_metadata?.first_name;
  const fullNameFromDb = profile?.first_name && profile?.last_name
    ? `${profile.first_name} ${profile.last_name}`
    : undefined;

  const initials = getInitials(
    firstName,
    fullNameFromDb || user?.user_metadata?.full_name,
    user?.email
  );
  const sidebarName = getSidebarName(
    firstName,
    user?.email
  );
  const fullName = getFullDisplayName(
    firstName,
    fullNameFromDb || user?.user_metadata?.full_name,
    user?.email
  );

  const handleSignOut = async () => {
    await firebaseSignOut(firebaseAuth);
    navigate('/login');
  };

  return (
    <div className="border-t dark:border-gray-700 p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
      <DropdownMenu onOpenChange={onDropdownOpenChange}>
        <DropdownMenuTrigger asChild>
          <button
            className={cn(
              'w-full flex items-center gap-2 rounded-lg px-2 py-2 min-h-[44px] md:min-h-0',
              'hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors',
              'text-gray-700 dark:text-gray-200',
              !isExpanded && 'justify-center px-0',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F37022] focus-visible:ring-offset-1'
            )}
          >
            <Avatar className="h-7 w-7 shrink-0">
              <AvatarFallback className="bg-[#F37022] text-white text-xs font-medium">
                {initials}
              </AvatarFallback>
            </Avatar>
            {isExpanded && (
              <>
                <span className="text-sm truncate flex-1 text-left">{sidebarName}</span>
                <ChevronUp className="h-3.5 w-3.5 shrink-0 text-gray-400" />
              </>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side={isExpanded ? 'top' : 'right'}
          align="start"
          className="w-56 dark:bg-gray-800 dark:border-gray-700"
          sideOffset={8}
          collisionPadding={8}
        >
          <DropdownMenuLabel className="font-normal">
            <div className="flex flex-col space-y-1">
              <p className="text-sm font-medium leading-none dark:text-gray-100">
                {fullName}
              </p>
              {user?.email && (
                <p className="text-xs leading-none text-muted-foreground dark:text-gray-400">
                  {user.email}
                </p>
              )}
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator className="dark:bg-gray-700" />
          <DropdownMenuItem
            className="cursor-pointer dark:hover:bg-gray-700 dark:text-gray-200"
            onClick={() => navigate('/settings/account')}
          >
            <Settings className="mr-2 h-4 w-4" />
            Settings
          </DropdownMenuItem>
          <DropdownMenuItem
            className="cursor-pointer dark:hover:bg-gray-700 dark:text-gray-200"
            onClick={() => navigate('/settings')}
          >
            <CreditCard className="mr-2 h-4 w-4" />
            Subscription
          </DropdownMenuItem>
          <DropdownMenuItem
            className="cursor-pointer dark:hover:bg-gray-700 dark:text-gray-200"
            onClick={() => window.location.href = 'mailto:support@jdsimplified.com'}
          >
            <LifeBuoy className="mr-2 h-4 w-4" />
            Support
          </DropdownMenuItem>
          <DropdownMenuSeparator className="dark:bg-gray-700" />
          <DropdownMenuItem
            className="cursor-pointer text-red-600 dark:text-red-400 dark:hover:bg-gray-700"
            onClick={handleSignOut}
          >
            <LogOut className="mr-2 h-4 w-4" />
            Sign Out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
});
